import { useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  AncestorIdData,
  AncestorNameData,
  FolderId,
  SubfolderIdData,
  SubfolderNameData,
} from 'modules/domains/foldering';
import { body2 } from 'ui/theme/typography';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { ChildItem, TreeViewQueryResponse } from './types';
import { TreeFolderLabel } from './TreeFolderLabel';
import { TreeSeed } from './TreeSeed';

export type TreeFolderProps = {
  ancestors: (AncestorIdData & AncestorNameData)[];
  /** check if this folder is a valid target */
  checkIfValid?: (folderId: FolderId) => boolean;
  /** the ID of the folder */
  id: FolderId;
  /** a counter that marks the level of indentation, used for styling */
  indentLevel: number;
  /** the name of the folder, useful to avoid lag in fetching data */
  name: string;
  /** the query to use to retrieve additional folder information */
  query: (folderId: FolderId) => TreeViewQueryResponse;
  /** Root folder id */
  rootFolderId: FolderId;
  /** currently entered text to highlight */
  searchText: string;
  /** the currently selected folder (as an ancestor path) in the tree view */
  selectedAncestors: (AncestorIdData & AncestorNameData)[];
  /** the currently selected folder id in the tree view */
  selectedFolderId: FolderId;
  /** update the selected folder id to a new value */
  setSelectedFolder: (
    folderId: FolderId,
    name: string,
    ancestors?: (AncestorIdData & AncestorNameData)[],
  ) => void;
};

type StyledIndentProps = {
  indentLevel: number;
};
const IndentContainer = styled.div`
    border-left: solid ${({ indentLevel }: StyledIndentProps) => (indentLevel > 1 ? spacing.single : pxToRem(0))} ${colors.grey15};
    margin-left: ${({ indentLevel }: StyledIndentProps) =>
      indentLevel > 1 ? spacing.plusHalf : pxToRem(0)};
`;

const StyledFolder = styled.div`
  ${body2}
`;

const StyledItemsList = styled.div`
  color: ${colors.textDisabled};
  padding: ${spacing.half} ${spacing.base};
`;

export const TreeFolder = ({
  ancestors = [],
  checkIfValid = () => false,
  id,
  indentLevel,
  name = '',
  query,
  rootFolderId,
  searchText = '',
  selectedAncestors,
  selectedFolderId,
  setSelectedFolder,
}: TreeFolderProps) => {
  const isRootFolder = id === rootFolderId;
  const isSelected = !isRootFolder && selectedFolderId === id;
  const isFolderOpen = useCallback(
    (folderId: FolderId) => {
      return !!selectedAncestors.find((ancestor) => ancestor.id === folderId);
    },
    [selectedAncestors],
  );
  const [isOpen, setIsOpen] = useState(isFolderOpen(id));
  const [childFolders, setChildFolders] = useState<(SubfolderIdData & SubfolderNameData)[]>([]);
  const [childItems, setChildItems] = useState<ChildItem[]>([]);
  const isDisabled = !checkIfValid(id);

  return (
    <StyledFolder data-test={`tree-folder-${id}`} role="group">
      {!isRootFolder && (
        <TreeFolderLabel
          ancestors={ancestors}
          id={id}
          isDisabled={isDisabled}
          isOpen={isOpen}
          isSelected={isSelected}
          name={name}
          searchText={searchText}
          setIsOpen={setIsOpen}
          setSelectedFolder={setSelectedFolder}
        />
      )}
      {!isDisabled && (isRootFolder || isOpen) && (
        <IndentContainer data-test="indent-container" indentLevel={indentLevel + 1}>
          <TreeSeed
            id={id}
            query={query}
            setChildItems={setChildItems}
            setChildFolders={setChildFolders}
          />
          {childFolders.map((childFolder) => (
            <TreeFolder
              ancestors={[...ancestors, { id, name }]}
              checkIfValid={checkIfValid}
              id={childFolder.id}
              indentLevel={indentLevel + 1}
              key={childFolder.id}
              name={childFolder.name}
              query={query}
              rootFolderId={rootFolderId}
              searchText={searchText}
              selectedAncestors={selectedAncestors}
              selectedFolderId={selectedFolderId}
              setSelectedFolder={setSelectedFolder}
            />
          ))}
          {!searchText &&
            childItems.map((childItem: ChildItem) => (
              <StyledItemsList key={childItem.id} role="treeitem" tabIndex={-1}>
                {childItem.name}
              </StyledItemsList>
            ))}
        </IndentContainer>
      )}
    </StyledFolder>
  );
};
